import React, { useState, useEffect } from 'react';
import { useFAQ } from '../context/FAQContext';

/*
  COMMENTS FOR BACKEND:

  1) We now rely on each FAQ item having a "category" field. The possible values are:
     - "General"
     - "Equipment and Generator"
     - "Election Day"

  2) Our client-side code filters `faqData` by `faq.category === activeTab`,
     so only items matching the selected tab are shown.

  3) Make sure /api/getFAQs (or the relevant endpoint) returns
     each FAQ item with the new "category" property set properly.

  4) We additionally show or hide category tabs based on "dj.hired_for_election_status".
*/

const FAQ = ({ onClose, dj }) => {
  const [openQuestion, setOpenQuestion] = useState(null);
  const [activeTab, setActiveTab] = useState('General');
  const { faqData, fetchFAQs } = useFAQ();

  useEffect(() => {
    // Fetch all active FAQ items once on mount
    fetchFAQs();
  }, [dj, fetchFAQs]);

  // Toggles the question open/closed
  const toggleQuestion = (id) => {
    setOpenQuestion(openQuestion === id ? null : id);
  };

  /**
   * If "dj.hired_for_election_status" is true, show all tabs;
   * otherwise, only show "General" tab.
   */
  const categories = dj?.hired_for_election_status
    ? ['General', 'Generator', 'Election Day']
    : ['General'];

  // Convert the fetched data to an array
  const faqItems = Array.isArray(faqData) ? faqData : [];

  // Filter FAQ items based on the currently active tab, then sort by the
  // `order` field (ascending, starting from 1). `order` may arrive as a string
  // from QuickBase, so coerce it; items without a valid order fall to the end.
  const filteredFAQItems = faqItems
    .filter((faq) => faq.category === activeTab)
    .sort((a, b) => {
      const orderA = Number(a.order);
      const orderB = Number(b.order);
      const safeA = Number.isFinite(orderA) ? orderA : Infinity;
      const safeB = Number.isFinite(orderB) ? orderB : Infinity;
      return safeA - safeB;
    });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        
        {/* Close Button Row */}
        <div className="p-4 flex justify-end">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 
                     focus:ring-[var(--primary-color)] focus:ring-offset-2 rounded-full p-1"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
        </div>

        {/* Title and Email Section */}
        <div className="px-6 pb-6 text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Frequently Asked Questions
          </h2>
          <div className="text-sm text-gray-500">
            Still have questions? Email us at{' '}
            <a href="mailto:djs@djsatthepolls.org" className="text-blue-600 hover:underline">
              djs@djsatthepolls.org
            </a>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-center space-x-2 sm:space-x-4">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              className={`
                px-4 py-2 text-sm font-medium rounded-full transition-colors
                ${
                  activeTab === cat
                    ? 'bg-[var(--primary-color)] text-white' // active styling
                    : 'border border-[var(--primary-color)] text-[var(--primary-color)] hover:bg-[var(--primary-color)] hover:text-white'
                }
              `}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Main FAQ Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-10rem)] pb-20">
          <div className="space-y-4">
            {filteredFAQItems.map((faq) => {
              const faqId = faq.id || faq._id; // fallback if there's an _id

              return (
                <div
                  key={faqId}
                  className="border border-gray-200 rounded-lg overflow-hidden 
                             transition-all duration-200 hover:border-[var(--primary-color)]"
                >
                  <button
                    onClick={() => toggleQuestion(faqId)}
                    className="w-full px-6 py-4 text-left flex justify-between items-center 
                               focus:outline-none focus:ring-2 focus:ring-inset 
                               focus:ring-[var(--primary-color)]"
                  >
                    <span className="text-lg font-medium text-gray-900">
                      {faq.question}
                    </span>
                    <div className="flex-shrink-0 w-8 flex items-center justify-center">
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                          openQuestion === faqId ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="2" 
                          d="M19 9l-7 7-7-7" 
                        />
                      </svg>
                    </div>
                  </button>
                  {openQuestion === faqId && (
                    <div className="px-6 py-4 bg-gray-50">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}

            {/* If no FAQs match the active category, show a message */}
            {filteredFAQItems.length === 0 && (
              <p className="text-gray-500">
                No FAQs found for this category.
              </p>
            )}
          </div>
        </div>

  
      </div>
    </div>
  );
};

export default FAQ;
